import type {
  FormField,
  FormFieldValue,
  FormSection,
} from '@/screens/ReportCreation/reportCreation.types';

/**
 * Conditional visibility helpers.
 *
 * Form fields can declare `{ dependsOn, visibleWhen }` which keeps the UI purely
 * declarative: the renderer only needs to ask "is this field visible?" while
 * these helpers watch every dependency and expose a map of values.
 * This allows repeatable sections, nested field names, and any number of
 * conditions to share the same infrastructure.
 */

export type DependencyValue = FormFieldValue;

export const ABSOLUTE_DEPENDENCY_PREFIX = '^';

export const isAbsoluteDependency = (name: string): boolean =>
  name.startsWith(ABSOLUTE_DEPENDENCY_PREFIX);

export const normalizeDependencyName = (name: string): string =>
  isAbsoluteDependency(name) ? name.slice(1) : name;

export const getConditionalDependencyNames = (
  sections: FormSection[],
): string[] => {
  const dependencies = new Set<string>();

  sections.forEach((section) => {
    section.fields.forEach((field: FormField) => {
      if (field.conditional?.dependsOn) {
        dependencies.add(normalizeDependencyName(field.conditional.dependsOn));
      }

      if (
        (field.type === 'select' || field.type === 'autocomplete-search') &&
        field.dynamicOptions?.dependsOn
      ) {
        dependencies.add(
          normalizeDependencyName(field.dynamicOptions.dependsOn),
        );
      }
    });
  });

  return Array.from(dependencies);
};

export const buildDependencyValueMap = (
  dependencyNames: string[],
  dependencyValues: DependencyValue[],
): Record<string, DependencyValue> => {
  return dependencyNames.reduce<Record<string, DependencyValue>>(
    (map, dependencyName, index) => {
      map[dependencyName] = dependencyValues?.[index];
      return map;
    },
    {},
  );
};
